import React, { useMemo, useState } from "react";
import { salesApi } from "../../../services/reportsService";
import { useReportData } from "../useReportData";
import ServerTable from "../ServerTable";
import {
  AlertStrip,
  BarList,
  ColumnChart,
  DonutChart,
  ErrorState,
  Heatmap,
  ItemList,
  KpiGrid,
  Leaderboard,
  Panel,
  ReportSkeleton,
  Row,
  SeriesLegend,
  StatusBadge,
  TrendChart,
  TablePanel,
  DistTable,
  TrendTable,
  cols,
  useReportContext,
} from "../ReportKit";
import {
  PALETTE,
  buildKpis,
  formatDate,
  formatValue,
  parseDate,
  toDist,
} from "../reportUtils";

const DEFS = [
  {
    key: "totalClients",
    label: "All clients",
    desc: "Everyone you work with",
    icon: "mdi:account-group-outline",
  },
  {
    key: "newClients",
    label: "New clients",
    desc: "Added {p}",
    prev: "previousNewClients",
    icon: "mdi:account-plus-outline",
  },
  {
    key: "activeClients",
    label: "Busy clients",
    desc: "Had enquiries or news {p}",
    icon: "mdi:account-check-outline",
  },
  {
    key: "dormantClients",
    label: "Quiet clients",
    desc: "No news for a long time",
    good: "DOWN",
    icon: "mdi:account-clock-outline",
  },
  {
    key: "unassignedClients",
    label: "No owner",
    desc: "Clients nobody looks after yet",
    good: "DOWN",
    icon: "mdi:account-question-outline",
  },
  {
    key: "avgEnquiriesPerClient",
    label: "Enquiries per client",
    desc: "Average for each client {p}",
    unit: "DECIMAL",
    icon: "mdi:file-multiple-outline",
  },
  {
    key: "repeatClientRate",
    label: "Clients who come back",
    desc: "Out of every 100 clients",
    unit: "PERCENT",
    icon: "mdi:repeat",
  },
  {
    key: "topOwnerClientCount",
    label: "Most clients",
    desc: "Held by one person",
    icon: "mdi:crown-outline",
  },
];

const ClientsView = ({ params, setFilter, onLoaded, refreshKey }) => {
  const { view } = useReportContext();
  const [onlyNew, setOnlyNew] = useState(false);
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const { data, loading, error, reload } = useReportData(
    () => salesApi.clientSummary(params),
    [JSON.stringify(params), refreshKey],
    onLoaded,
  );
  const tableParams = useMemo(
    () => ({
      startDate: params.startDate,
      endDate: params.endDate,
      ownerId: params.ownerId,
      source: params.source,
      industry: params.industry,
      isNew: onlyNew || undefined,
      unassigned: onlyUnassigned || undefined,
    }),
    [params, onlyNew, onlyUnassigned],
  );

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <ReportSkeleton />;

  const { meta = {}, charts = {}, lists = {} } = data;
  const currency = meta.currency || "INR";
  const owners = [...(charts.byOwner || [])].sort(
    (a, b) => (b.totalClients || 0) - (a.totalClients || 0),
  );
  const kpis = buildKpis(data.kpis, DEFS).map((k) =>
    k.key === "topOwnerClientCount"
      ? {
          ...k,
          desc: owners[0]?.ownerName
            ? `Held by ${owners[0].ownerName}`
            : k.desc,
        }
      : k,
  );
  const unassigned =
    kpis.find((k) => k.key === "unassignedClients")?.value || 0;
  const dormant = kpis.find((k) => k.key === "dormantClients")?.value || 0;
  const newSeries = [
    { key: "newClients", label: "New clients", color: PALETTE[0], type: "bar" },
  ];
  const growthSeries = [
    {
      key: "cumulativeClients",
      label: "Total clients",
      color: PALETTE[1],
      type: "area",
    },
  ];
  const start = parseDate(params.startDate);
  const isNew = (r) => r.createdAt && new Date(r.createdAt) >= start;

  const columns = [
    {
      key: "clientName",
      header: "Client",
      sortable: true,
      render: (v, r) => (
        <div className="rp-cell-main">
          <strong>{v}</strong>
          {isNew(r) && <em className="rp-new">New</em>}
        </div>
      ),
    },
    { key: "industry", header: "Industry", sortable: true },
    { key: "source", header: "Source", sortable: true },
    {
      key: "assignedEmployeeName",
      header: "Owner",
      render: (v) => v || <span className="rp-muted">Unassigned</span>,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      render: (v) => formatDate(v),
    },
    { key: "enquiryCount", header: "Enquiries", align: "right" },
    { key: "openEnquiries", header: "Open", align: "right" },
    { key: "approvedEnquiries", header: "Approved", align: "right" },
    { key: "rejectedEnquiries", header: "Rejected", align: "right" },
    {
      key: "totalValue",
      header: "Value",
      align: "right",
      render: (v) => formatValue(v, "CURRENCY", currency),
    },
    {
      key: "status",
      header: "Status",
      render: (v) => <StatusBadge value={v} />,
    },
  ];

  const base = {
    startDate: params.startDate,
    endDate: params.endDate,
    ownerId: params.ownerId,
    source: params.source,
    industry: params.industry,
  };
  const T = (title, how, p = {}, extra = {}) => ({
    modal: {
      title,
      how,
      columns,
      fetcher: salesApi.clientTable,
      params: { ...base, ...p },
      sort: "createdAt,desc",
      searchable: true,
      ...extra,
    },
  });
  const details = {
    totalClients: {
      scrollTo: "tbl-main",
      ...T("All clients", "These are all the clients you can see."),
    },
    newClients: T("New clients", "These are the clients added {p}.", {
      isNew: true,
    }),
    activeClients: T(
      "Busy clients",
      "These clients had enquiries or news {p}.",
      {},
      { filter: (r) => /^ACTIVE$/i.test(r.status || "") },
    ),
    dormantClients: {
      scrollTo: "quiet-clients",
      ...T(
        "Quiet clients",
        "These clients have had no news for a long time.",
        {},
        { filter: (r) => /^DORMANT$/i.test(r.status || "") },
      ),
    },
    unassignedClients: T(
      "Clients with no owner",
      "Nobody looks after these clients yet.",
      { unassigned: true },
    ),
    avgEnquiriesPerClient: T(
      "Clients with enquiries",
      "Add up the enquiries of these clients and divide by the number of clients to get the average.",
      {},
      { filter: (r) => Number(r.enquiryCount) > 0 },
    ),
    repeatClientRate: T(
      "Clients who came back",
      "These clients have two or more approved enquiries.",
      {},
      { filter: (r) => Number(r.approvedEnquiries) >= 2 },
    ),
    topOwnerClientCount: { scrollTo: "client-owners" },
  };

  return (
    <div className={`rp-view ${loading ? "rp-refreshing" : ""}`}>
      <AlertStrip
        items={[
          unassigned > 0 && {
            tone: "warn",
            icon: "mdi:account-question-outline",
            text: `${unassigned} clients have no owner`,
          },
          dormant > 0 && {
            tone: "warn",
            icon: "mdi:account-clock-outline",
            text: `${dormant} clients have gone quiet`,
          },
        ]}
      />
      <KpiGrid kpis={kpis} currency={currency} details={details} />
      {view === "table" ? (
        <>
          <ServerTable
            id="tbl-main"
            title="All clients"
            columns={columns}
            params={tableParams}
            fetcher={salesApi.clientTable}
            defaultSort="createdAt,desc"
            exportName="clients"
            onData={onLoaded}
            toolbar={
              <>
                <label className="rp-check">
                  <input
                    type="checkbox"
                    checked={onlyNew}
                    onChange={(e) => setOnlyNew(e.target.checked)}
                  />{" "}
                  New only
                </label>
                <label className="rp-check">
                  <input
                    type="checkbox"
                    checked={onlyUnassigned}
                    onChange={(e) => setOnlyUnassigned(e.target.checked)}
                  />{" "}
                  Unassigned
                </label>
              </>
            }
          />

          <div className="rp-tables">
            <TrendTable
              title="New clients by date"
              data={charts.newClientsTrend}
              series={newSeries}
              granularity={meta.granularity}
            />
            <TrendTable
              title="All clients by date"
              data={charts.clientGrowth}
              series={growthSeries}
              granularity={meta.granularity}
            />
            <TablePanel
              id="client-owners"
              title="Who has the most clients"
              exportName="client-owners"
              columns={[
                cols.text("name", "Owner"),
                cols.num("total", "All clients"),
                cols.num("fresh", "New"),
                cols.num("active", "Busy"),
                cols.num("enq", "Enquiries"),
                cols.num("won", "Money won", "CURRENCY", currency),
              ]}
              rows={owners.map((o) => ({
                name: o.ownerName,
                total: o.totalClients,
                fresh: o.newClients,
                active: o.activeClients,
                enq: o.enquiryCount,
                won: o.wonValue,
              }))}
            />
            <DistTable
              title="Where clients come from"
              data={toDist(charts.bySource)}
              nameHeader="Source"
              valueHeader="Clients"
            />
            <DistTable
              title="By industry"
              data={toDist(charts.byIndustry)}
              nameHeader="Industry"
              valueHeader="Clients"
            />
            <DistTable
              title="By region"
              data={toDist(charts.byRegion)}
              nameHeader="Region"
              valueHeader="Clients"
            />
            <DistTable
              title="How long clients have been with you"
              data={toDist(charts.clientAgeBuckets)}
              nameHeader="Time"
              valueHeader="Clients"
            />
            <TablePanel
              title="Top clients by money"
              exportName="top-clients"
              columns={[
                cols.text("clientName", "Client"),
                cols.text("industry", "Industry"),
                cols.text("assignedEmployeeName", "Owner"),
                cols.num("wonValue", "Money won", "CURRENCY", currency),
                cols.num("enquiryCount", "Enquiries"),
              ]}
              rows={charts.topClientsByValue || []}
            />
            <TablePanel
              title="Newly added clients"
              exportName="new-clients"
              columns={[
                cols.text("clientName", "Client"),
                cols.text("industry", "Industry"),
                cols.text("source", "Source"),
                cols.text("assignedEmployeeName", "Owner"),
                cols.date("createdAt", "Added"),
              ]}
              rows={lists.recentClients || []}
            />
            <TablePanel
              id="quiet-clients"
              title="Quiet clients"
              exportName="quiet-clients"
              columns={[
                cols.text("clientName", "Client"),
                cols.text("assignedEmployeeName", "Owner"),
                cols.date("lastActivityAt", "Last news"),
                cols.num("daysSinceActivity", "Days quiet"),
              ]}
              rows={lists.dormantClientsTop || []}
            />
            <Panel title="Source by industry" subtitle="Clients for each pair">
              <Heatmap matrix={charts.sourceByIndustry?.rows} />
            </Panel>
          </div>
        </>
      ) : (
        <>
          <Row layout="1-1">
            <Panel
              title="New clients"
              subtitle="Added in each period"
              actions={<SeriesLegend series={newSeries} />}
            >
              <TrendChart
                data={charts.newClientsTrend}
                series={newSeries}
                granularity={meta.granularity}
              />
            </Panel>
            <Panel title="Client growth" subtitle="Running total">
              <TrendChart
                data={charts.clientGrowth}
                series={growthSeries}
                granularity={meta.granularity}
              />
            </Panel>
          </Row>

          <Row layout="2-1">
            <Panel
              id="client-owners"
              title="Who has the most clients"
              subtitle="Owners ranked by total clients"
            >
              <Leaderboard
                primaryLabel="clients"
                currency={currency}
                rows={owners.map((o) => ({
                  name: o.ownerName,
                  primary: Number(o.totalClients) || 0,
                  metrics: [
                    { label: "New", value: o.newClients },
                    { label: "Active", value: o.activeClients },
                    { label: "Enquiries", value: o.enquiryCount },
                    { label: "Won", value: o.wonValue, unit: "CURRENCY" },
                  ],
                }))}
              />
            </Panel>
            <Panel
              title="Where clients come from"
              subtitle="Select a source to filter"
            >
              <DonutChart
                data={toDist(charts.bySource)}
                onSelect={(v) => setFilter("source", v)}
              />
            </Panel>
          </Row>

          <Row layout="1-1-1">
            <Panel title="By industry">
              <BarList
                data={toDist(charts.byIndustry)}
                color={PALETTE[3]}
                onSelect={(v) => setFilter("industry", v)}
              />
            </Panel>
            <Panel title="By region">
              <BarList data={toDist(charts.byRegion)} color={PALETTE[5]} />
            </Panel>
            <Panel title="Client age" subtitle="Days since added">
              <ColumnChart
                data={toDist(charts.clientAgeBuckets)}
                color={PALETTE[2]}
                height={200}
              />
            </Panel>
          </Row>

          <Row layout="1-1">
            <Panel
              title="Source by industry"
              subtitle="Clients per combination"
            >
              <Heatmap matrix={charts.sourceByIndustry?.rows} />
            </Panel>
            <Panel title="Top clients by value">
              <BarList
                unit="CURRENCY"
                currency={currency}
                color={PALETTE[1]}
                data={(charts.topClientsByValue || []).map((c) => ({
                  name: c.clientName,
                  key: c.clientId,
                  value: Number(c.wonValue) || 0,
                }))}
              />
            </Panel>
          </Row>

          <Row layout="1-1">
            <Panel title="Newly added clients" subtitle="Most recent first">
              <ItemList
                empty="No clients added in this period"
                items={(lists.recentClients || []).map((c) => ({
                  id: c.clientId,
                  title: c.clientName,
                  subtitle: [c.industry, c.source].filter(Boolean).join(", "),
                  right: `${c.assignedEmployeeName || "Unassigned"}, ${formatDate(c.createdAt)}`,
                }))}
              />
            </Panel>
            <Panel
              id="quiet-clients"
              title="Gone quiet"
              subtitle="Longest without activity"
            >
              <ItemList
                empty="No dormant clients"
                items={(lists.dormantClientsTop || []).map((c) => ({
                  id: c.clientId,
                  title: c.clientName,
                  subtitle: c.assignedEmployeeName || "Unassigned",
                  right: `${c.daysSinceActivity ?? "—"} days quiet`,
                }))}
              />
            </Panel>
          </Row>
        </>
      )}
    </div>
  );
};

export default ClientsView;
